import { sendInvitationSetupEmail } from "./authService";
import { supabase } from "./client";
import { withRequestDeadline } from "./requestDeadline";
import {
  isSupabaseConfigured,
  missingSupabaseConfig,
} from "./config";

const ensureSupabaseReady = () => {
  if (!isSupabaseConfigured || !supabase) {
    throw new Error(
      `Faltan variables de entorno para Supabase: ${missingSupabaseConfig.join(", ")}`
    );
  }
};

const normalizeError = (error, fallbackMessage) => {
  const message = String(error?.message || "").trim();
  const details = String(error?.details || "").trim();

  if (
    error?.code === "42P01" ||
    error?.code === "PGRST202" ||
    message.includes("collaborator_profiles") ||
    message.includes("create_collaborator_access_invitation")
  ) {
    return new Error(
      "La base de datos de colaboradores todavia no esta actualizada. Aplica la migracion antes de emitir invitaciones."
    );
  }

  return new Error(message || details || fallbackMessage);
};

const normalizeText = (value, maxLength = 180) =>
  String(value || "").trim().slice(0, maxLength);

const normalizeEmail = (value) => normalizeText(value, 180).toLowerCase();

export const isValidCollaboratorEmail = (value) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(normalizeEmail(value));

const toIsoDate = (value) => {
  const normalized = normalizeText(value, 40);
  return normalized ? normalized.slice(0, 10) : "";
};

export const getCollaboratorPortalOrigin = (value = "") => {
  const fallbackOrigin =
    typeof window === "undefined" ? "https://mozzoapp.com" : window.location.origin;
  const requestedOrigin = normalizeText(value, 240) || fallbackOrigin;

  try {
    const url = new URL(requestedOrigin);
    if (url.hostname === "backoffice.mozzoapp.com") {
      url.hostname = "mozzoapp.com";
    } else if (url.hostname === "backoffice.beta.mozzoapp.com") {
      url.hostname = "beta.mozzoapp.com";
    }
    return url.origin;
  } catch {
    return requestedOrigin.replace(/\/$/, "");
  }
};

export const mapCollaboratorProfileRow = (row) => {
  if (!row) {
    return null;
  }

  return {
    id: row.id,
    userId: row.user_id || "",
    role: row.role === "star" ? "star" : "normal",
    invitedByProfileId: row.invited_by_profile_id || "",
    email: normalizeEmail(row.email),
    firstName: normalizeText(row.first_name, 80),
    lastName: normalizeText(row.last_name, 80),
    phone: normalizeText(row.phone, 50),
    cuit: normalizeText(row.cuit, 30),
    taxCondition: normalizeText(row.tax_condition, 80),
    fiscalAddress: normalizeText(row.fiscal_address, 160),
    taxStatus: normalizeText(row.tax_status, 120),
    payoutAlias: normalizeText(row.payout_alias, 80),
    sellerCode: normalizeText(row.seller_code, 40),
    status: normalizeText(row.status, 40) || "pending_validation",
    joinedAt: toIsoDate(row.joined_at || row.created_at),
    contractAcceptance:
      row.contract_acceptance && typeof row.contract_acceptance === "object"
        ? row.contract_acceptance
        : undefined,
  };
};

export const mapCollaboratorInvitationRow = (row, origin = "") => {
  if (!row) {
    return null;
  }

  const normalizedOrigin = getCollaboratorPortalOrigin(origin);
  const code = normalizeText(row.code, 50).toUpperCase();
  const email = normalizeEmail(row.email);

  return {
    id: row.id,
    role: row.role === "star" ? "star" : "normal",
    starProfileId: row.star_profile_id || "",
    profileId: row.profile_id || "",
    name: normalizeText(row.name, 160),
    email,
    code,
    status: normalizeText(row.status, 30) || "pending",
    emailAttempts: Number(row.email_attempts || 0),
    lastEmailSentAt: row.last_email_sent_at || "",
    emailSendError: normalizeText(row.email_send_error, 500),
    acceptedAt: row.accepted_at || "",
    createdAt: toIsoDate(row.created_at),
    registrationUrl: normalizedOrigin
      ? `${normalizedOrigin}/colaboradores/registro?code=${encodeURIComponent(
          code
        )}&email=${encodeURIComponent(email)}`
      : "",
  };
};

const mapInvitationPayload = (payload, origin = "") => ({
  profile: mapCollaboratorProfileRow(payload?.profile),
  invitation: mapCollaboratorInvitationRow(payload?.invitation, origin),
});

const splitFullName = (name) => {
  const parts = normalizeText(name, 160).split(/\s+/).filter(Boolean);
  return {
    firstName: parts.shift() || "",
    lastName: parts.join(" ") || "Sin apellido",
  };
};

const markInvitationDelivery = async (invitationId, sent, errorMessage = "") => {
  if (!invitationId) {
    return;
  }

  const { error } = await supabase.rpc("mark_collaborator_invitation_delivery", {
    p_invitation_id: invitationId,
    p_sent: sent === true,
    p_error: normalizeText(errorMessage, 500),
  });

  if (error) {
    console.warn("No pudimos registrar el estado del correo de colaborador:", error);
  }
};

export const deliverCollaboratorAccessEmail = async ({
  invitation,
  origin = typeof window === "undefined" ? "" : window.location.origin,
}) => {
  ensureSupabaseReady();

  const normalizedInvitation = {
    ...invitation,
    registrationUrl:
      invitation?.registrationUrl ||
      mapCollaboratorInvitationRow(
        {
          ...invitation,
          star_profile_id: invitation?.starProfileId,
          profile_id: invitation?.profileId,
          email_attempts: invitation?.emailAttempts,
          last_email_sent_at: invitation?.lastEmailSentAt,
          email_send_error: invitation?.emailSendError,
          accepted_at: invitation?.acceptedAt,
          created_at: invitation?.createdAt,
        },
        origin
      )?.registrationUrl,
  };

  if (
    !normalizedInvitation?.id ||
    !normalizedInvitation?.email ||
    !normalizedInvitation?.code ||
    !normalizedInvitation?.registrationUrl
  ) {
    throw new Error("La invitacion no tiene los datos necesarios para enviar el correo.");
  }

  try {
    await sendInvitationSetupEmail({
      email: normalizedInvitation.email,
      redirectTo: normalizedInvitation.registrationUrl,
      userData: {
        workspace_role:
          normalizedInvitation.role === "star"
            ? "collaborator_star"
            : "collaborator",
        collaborator_invitation_id: normalizedInvitation.id,
        collaborator_invitation_code: normalizedInvitation.code,
      },
    });
    await markInvitationDelivery(normalizedInvitation.id, true);
    return {
      sent: true,
      message: `Enviamos el acceso a ${normalizedInvitation.email}.`,
      invitation: {
        ...normalizedInvitation,
        emailAttempts: Number(normalizedInvitation.emailAttempts || 0) + 1,
        lastEmailSentAt: new Date().toISOString(),
        emailSendError: "",
      },
    };
  } catch (error) {
    const normalizedError = normalizeError(
      error,
      "No pudimos enviar el correo de acceso al colaborador."
    );
    await markInvitationDelivery(
      normalizedInvitation.id,
      false,
      normalizedError.message
    );
    return {
      sent: false,
      message: normalizedError.message,
      invitation: {
        ...normalizedInvitation,
        emailAttempts: Number(normalizedInvitation.emailAttempts || 0) + 1,
        emailSendError: normalizedError.message,
      },
    };
  }
};

export const createCollaboratorAccessInvitation = async ({
  role,
  firstName,
  lastName,
  name,
  email,
  phone = "",
  cuit = "",
  payoutAlias = "",
  activateNow = false,
  origin = typeof window === "undefined" ? "" : window.location.origin,
}) => {
  ensureSupabaseReady();

  const parsedName = splitFullName(name);
  const normalizedFirstName = normalizeText(firstName, 80) || parsedName.firstName;
  const normalizedLastName = normalizeText(lastName, 80) || parsedName.lastName;
  const normalizedEmail = normalizeEmail(email);

  if (!normalizedFirstName || !normalizedLastName || !normalizedEmail) {
    throw new Error("Completa nombre, apellido y correo del colaborador.");
  }

  if (!isValidCollaboratorEmail(normalizedEmail)) {
    throw new Error(
      "El correo no parece completo. Revisa especialmente el punto antes de com (por ejemplo, nombre@gmail.com)."
    );
  }

  const { data, error } = await supabase.rpc(
    "create_collaborator_access_invitation",
    {
      p_role: role === "star" ? "star" : "normal",
      p_first_name: normalizedFirstName,
      p_last_name: normalizedLastName,
      p_email: normalizedEmail,
      p_phone: normalizeText(phone, 50),
      p_cuit: normalizeText(cuit, 30),
      p_payout_alias: normalizeText(payoutAlias, 80),
      p_activate_now: activateNow === true,
    }
  );

  if (error) {
    throw normalizeError(error, "No pudimos crear la invitacion del colaborador.");
  }

  const result = mapInvitationPayload(data, origin);
  const delivery = await deliverCollaboratorAccessEmail({
    invitation: result.invitation,
    origin,
  });

  return {
    ...result,
    invitation: delivery.invitation,
    delivery,
  };
};

export const listBackofficeCollaboratorAccess = async ({
  origin = typeof window === "undefined" ? "" : window.location.origin,
} = {}) => {
  ensureSupabaseReady();

  const [profilesResult, invitationsResult] = await Promise.all([
    supabase
      .from("collaborator_profiles")
      .select("*")
      .order("created_at", { ascending: false }),
    supabase
      .from("collaborator_invitations")
      .select("*")
      .order("created_at", { ascending: false }),
  ]);

  if (profilesResult.error) {
    throw normalizeError(
      profilesResult.error,
      "No pudimos cargar los colaboradores del backoffice."
    );
  }

  if (invitationsResult.error) {
    throw normalizeError(
      invitationsResult.error,
      "No pudimos cargar las invitaciones del backoffice."
    );
  }

  return {
    profiles: (profilesResult.data || []).map(mapCollaboratorProfileRow),
    invitations: (invitationsResult.data || []).map((row) =>
      mapCollaboratorInvitationRow(row, origin)
    ),
  };
};

export const loadCurrentCollaboratorAccess = async ({
  origin = typeof window === "undefined" ? "" : window.location.origin,
} = {}) => {
  ensureSupabaseReady();

  const [profilesResult, invitationsResult] = await withRequestDeadline((signal) => Promise.all([
    supabase
      .from("collaborator_profiles")
      .select("*")
      .order("created_at", { ascending: false })
      .abortSignal(signal),
    supabase
      .from("collaborator_invitations")
      .select("*")
      .order("created_at", { ascending: false })
      .abortSignal(signal),
  ]), { message: "No pudimos verificar tu acceso a tiempo. Vuelve a intentar." });

  if (profilesResult.error) {
    throw normalizeError(
      profilesResult.error,
      "No pudimos cargar tu perfil de colaborador."
    );
  }

  if (invitationsResult.error) {
    throw normalizeError(
      invitationsResult.error,
      "No pudimos cargar tu invitacion de colaborador."
    );
  }

  return {
    profiles: (profilesResult.data || []).map(mapCollaboratorProfileRow),
    invitations: (invitationsResult.data || []).map((row) =>
      mapCollaboratorInvitationRow(row, origin)
    ),
  };
};

export const findCollaboratorProfileForUser = (
  profiles = [],
  email = "",
  userId = ""
) => {
  const normalizedEmail = normalizeEmail(email);
  const normalizedUserId = normalizeText(userId, 80);

  if (!normalizedEmail || !normalizedUserId) {
    return null;
  }

  return (
    profiles.find(
      (profile) =>
        profile.email === normalizedEmail &&
        profile.userId === normalizedUserId
    ) || null
  );
};

export const findPendingCollaboratorInvitationForEmail = (
  invitations = [],
  email = ""
) => {
  const normalizedEmail = normalizeEmail(email);

  if (!normalizedEmail) {
    return null;
  }

  return (
    invitations.find(
      (invitation) =>
        invitation.email === normalizedEmail && invitation.status === "pending"
    ) || null
  );
};

export const acceptCollaboratorAccessInvitation = async (values = {}) => {
  ensureSupabaseReady();

  const { data, error } = await supabase.rpc(
    "accept_collaborator_access_invitation",
    {
      p_code: normalizeText(values.invitationCode, 50).toUpperCase(),
      p_first_name: normalizeText(values.firstName, 80),
      p_last_name: normalizeText(values.lastName, 80),
      p_phone: normalizeText(values.phone, 50),
      p_cuit: normalizeText(values.cuit, 30),
      p_payout_alias: normalizeText(values.payoutAlias, 80),
    }
  );

  if (error) {
    throw normalizeError(error, "No pudimos activar el perfil de colaborador.");
  }

  return mapInvitationPayload(data, window.location.origin);
};

export const resumeCurrentCollaboratorAccess = async ({
  email,
  userId,
  origin = typeof window === "undefined" ? "" : window.location.origin,
  values = {},
} = {}) => {
  const access = await loadCurrentCollaboratorAccess({ origin });
  const existingProfile = findCollaboratorProfileForUser(
    access.profiles,
    email,
    userId
  );

  if (existingProfile) {
    return {
      profile: existingProfile,
      invitation: null,
      resumed: false,
    };
  }

  const invitation = findPendingCollaboratorInvitationForEmail(
    access.invitations,
    email
  );
  if (!invitation) {
    return null;
  }

  const invitedProfile =
    access.profiles.find(
      (profile) =>
        profile.id === invitation.profileId || profile.email === invitation.email
    ) || null;
  const parsedName = splitFullName(invitation.name);

  const accepted = await acceptCollaboratorAccessInvitation({
    invitationCode: invitation.code,
    firstName: normalizeText(values.firstName, 80) ||
      invitedProfile?.firstName || parsedName.firstName,
    lastName: normalizeText(values.lastName, 80) ||
      invitedProfile?.lastName || parsedName.lastName,
    phone: normalizeText(values.phone, 50) || invitedProfile?.phone || "",
    cuit: normalizeText(values.cuit, 30) || invitedProfile?.cuit || "",
    payoutAlias:
      normalizeText(values.payoutAlias, 80) || invitedProfile?.payoutAlias || "",
  });

  return {
    ...accepted,
    resumed: true,
  };
};

export const updateCollaboratorAccessProfile = async (profileId, values = {}) => {
  ensureSupabaseReady();

  const payload = {};
  if (Object.prototype.hasOwnProperty.call(values, "firstName")) {
    payload.first_name = normalizeText(values.firstName, 80);
  }
  if (Object.prototype.hasOwnProperty.call(values, "lastName")) {
    payload.last_name = normalizeText(values.lastName, 80);
  }
  if (Object.prototype.hasOwnProperty.call(values, "phone")) {
    payload.phone = normalizeText(values.phone, 50);
  }
  if (Object.prototype.hasOwnProperty.call(values, "cuit")) {
    payload.cuit = normalizeText(values.cuit, 30);
  }
  if (Object.prototype.hasOwnProperty.call(values, "taxCondition")) {
    payload.tax_condition = normalizeText(values.taxCondition, 80);
  }
  if (Object.prototype.hasOwnProperty.call(values, "fiscalAddress")) {
    payload.fiscal_address = normalizeText(values.fiscalAddress, 160);
  }
  if (Object.prototype.hasOwnProperty.call(values, "payoutAlias")) {
    payload.payout_alias = normalizeText(values.payoutAlias, 80);
  }
  if (Object.prototype.hasOwnProperty.call(values, "contractAcceptance")) {
    payload.contract_acceptance = values.contractAcceptance || null;
  }
  if (Object.prototype.hasOwnProperty.call(values, "status")) {
    payload.status = normalizeText(values.status, 40);
  }
  if (Object.prototype.hasOwnProperty.call(values, "taxStatus")) {
    payload.tax_status = normalizeText(values.taxStatus, 120);
  }

  const { data, error } = await supabase
    .from("collaborator_profiles")
    .update(payload)
    .eq("id", profileId)
    .select("*")
    .single();

  if (error) {
    throw normalizeError(error, "No pudimos actualizar el perfil del colaborador.");
  }

  return mapCollaboratorProfileRow(data);
};
